import { useState, useCallback, useEffect, useRef } from "react";
import {
  services,
  faqs,
  contactInfo,
  welcomeMessage,
  fallbackResponse,
  humanHandoffKeywords,
  leadPrompt,
  leadConfirm,
  emailSentMessage,
  emailErrorMessage,
  invalidLeadInfo,
  quoteServicePrompt,
  quoteCancelled,
  quoteInvalidOption,
  quoteEmailPrompt,
  quoteEmailSent,
  quoteEmailNoThanks,
} from "../data/chatKnowledge";
import {
  getQuoteFlow,
  getServiceNames,
  matchServiceOption,
  formatQuoteSummary,
} from "../data/quoteFlow";
import { sendChatTranscript, sendQuoteEmail } from "../services/email";

const STORAGE_KEY = "carium_chat_history";
const LEAD_KEY = "carium_chat_lead";

let nextId = 1;

function loadMessages() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      nextId = parsed.length > 0 ? Math.max(...parsed.map((m) => m.id)) + 1 : 1;
      return parsed;
    }
  } catch {}
  return [];
}

function saveMessages(messages) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  } catch {}
}

function loadLead() {
  try {
    const raw = localStorage.getItem(LEAD_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {}
  return null;
}

function saveLead(lead) {
  try {
    if (lead) {
      localStorage.setItem(LEAD_KEY, JSON.stringify(lead));
    } else {
      localStorage.removeItem(LEAD_KEY);
    }
  } catch {}
}

function tokenize(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}

function scoreKeywords(inputTokens, keywordList) {
  let hits = 0;
  for (const kw of keywordList) {
    const kwTokens = kw.toLowerCase().split(/\s+/);
    if (kwTokens.length === 1) {
      if (inputTokens.includes(kwTokens[0])) hits++;
    } else {
      const joined = inputTokens.join(" ");
      if (joined.includes(kw.toLowerCase())) hits++;
    }
  }
  return hits;
}

function matchFAQ(inputTokens) {
  let best = null;
  let bestScore = 0;
  for (const faq of faqs) {
    const score = scoreKeywords(inputTokens, faq.keywords);
    if (score > bestScore) {
      bestScore = score;
      best = faq;
    }
  }
  return bestScore >= 1 ? best : null;
}

function matchServices(inputTokens) {
  const matches = [];
  for (const svc of services) {
    const score = scoreKeywords(inputTokens, svc.keywords);
    if (score >= 1) matches.push({ ...svc, score });
  }
  matches.sort((a, b) => b.score - a.score);
  return matches.slice(0, 3);
}

function matchContact(inputTokens) {
  const contactKeywords = [
    "contact", "reach", "email", "phone", "call", "whatsapp",
    "talk", "speak", "address", "location", "where", "get in touch",
  ];
  return scoreKeywords(inputTokens, contactKeywords) >= 1;
}

function matchGreeting(inputTokens) {
  const greetings = ["hello", "hi", "hey", "good morning", "good afternoon", "good evening", "sup", "yo", "hola"];
  return scoreKeywords(inputTokens, greetings) >= 1;
}

function matchHumanHandoff(inputTokens) {
  return scoreKeywords(inputTokens, humanHandoffKeywords) >= 1;
}

function matchQuoteTrigger(inputTokens) {
  const keywords = [
    "quote", "quotation", "estimate", "pricing", "price", "cost",
    "how much", "get a quote", "get quote", "get a price", "get a cost",
  ];
  return scoreKeywords(inputTokens, keywords) >= 1;
}

function parseLeadInfo(text) {
  const emailRegex = /[\w.+-]+@[\w-]+\.[\w.]+/;
  const emailMatch = text.match(emailRegex);
  if (!emailMatch) return null;

  const email = emailMatch[0];
  let name = text
    .replace(emailRegex, "")
    .replace(/[,;:]+/g, " ")
    .replace(/\b(my|name|is|i'm|i am|me|email|address|hey|hi|hello)\b/gi, "")
    .trim();

  name = name.replace(/\s+/g, " ").trim();

  if (name.length < 2) return null;
  return { name, email };
}

function buildWhatsAppUrl(leadInfo, messages) {
  const userMessages = messages
    .filter((m) => m.sender === "user")
    .slice(-5)
    .map((m) => m.text)
    .join("\n- ");

  const text = `Hello Carium Tech!\n\nI'm ${leadInfo.name} (${leadInfo.email}).\n\nHere's what I need help with:\n- ${userMessages || "General inquiry"}\n\nLooking forward to hearing from you!`;

  return `${contactInfo.whatsapp}?text=${encodeURIComponent(text)}`;
}

function buildQuoteWhatsAppUrl(leadInfo, quoteFlow) {
  const { service, answers, result } = quoteFlow;
  const period = result.period || "";
  const answerText = Object.entries(answers)
    .map(([, v]) => v)
    .join(", ");

  const text =
    `Hello Carium Tech! I'd like a detailed quote for ${service.serviceName}.\n\n` +
    `My selections: ${answerText}\n\n` +
    `Estimated range: KES ${result.min.toLocaleString()}${period} – ${result.max.toLocaleString()}${period}\n\n` +
    (leadInfo ? `Name: ${leadInfo.name}\nEmail: ${leadInfo.email}\n\n` : "") +
    `Please prepare a formal proposal. Thank you!`;

  return `${contactInfo.whatsapp}?text=${encodeURIComponent(text)}`;
}

function generateResponse(userText) {
  const inputTokens = tokenize(userText);

  if (matchGreeting(inputTokens)) return welcomeMessage;

  if (matchContact(inputTokens)) {
    return (
      `You can reach our engineering team directly:\n\n` +
      `Email: ${contactInfo.email}\n` +
      `Phone/WhatsApp: ${contactInfo.phone}\n` +
      `Location: ${contactInfo.location}\n\n` +
      `We typically respond within 24 hours. You can also use the Contact Us page on our website.`
    );
  }

  const faqMatch = matchFAQ(inputTokens);
  if (faqMatch) return faqMatch.answer;

  const serviceMatches = matchServices(inputTokens);
  if (serviceMatches.length > 0) {
    if (serviceMatches.length === 1) {
      const svc = serviceMatches[0];
      return (
        `Great choice! Here's what we offer in ${svc.name}:\n\n` +
        `${svc.description}\n\n` +
        `Would you like to discuss a project? Contact us at ${contactInfo.email}`
      );
    }
    const list = serviceMatches
      .map((s, i) => `${i + 1}. ${s.name} — ${s.description}`)
      .join("\n\n");
    return (
      `Here are the services that match your interest:\n\n${list}\n\n` +
      `Want to learn more or start a project? Contact us at ${contactInfo.email}`
    );
  }

  return fallbackResponse;
}

const initialQuoteState = {
  active: false,
  service: null,
  stepIndex: 0,
  answers: {},
  result: null,
  collectingEmail: false,
  emailSent: false,
};

export default function useChat() {
  const [messages, setMessages] = useState(loadMessages);
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [leadInfo, setLeadInfo] = useState(loadLead);
  const [isCollectingLead, setIsCollectingLead] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [quoteFlow, setQuoteFlow] = useState(initialQuoteState);
  const hasWelcomed = useRef(messages.length > 0);

  useEffect(() => {
    saveMessages(messages);
  }, [messages]);

  useEffect(() => {
    saveLead(leadInfo);
  }, [leadInfo]);

  const addMessage = useCallback((text, sender, extra = {}) => {
    const msg = { id: nextId++, text, sender, timestamp: Date.now(), ...extra };
    setMessages((prev) => [...prev, msg]);
    return msg;
  }, []);

  const sendWelcome = useCallback(() => {
    if (!hasWelcomed.current) {
      hasWelcomed.current = true;
      addMessage(welcomeMessage, "bot");
    }
  }, [addMessage]);

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) sendWelcome();
      return !prev;
    });
  }, [sendWelcome]);

  const closeChat = useCallback(() => setIsOpen(false), []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setLeadInfo(null);
    setIsCollectingLead(false);
    setQuoteFlow(initialQuoteState);
    setEmailSending(false);
    hasWelcomed.current = false;
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LEAD_KEY);
    } catch {}
  }, []);

  const sendEmailTranscript = useCallback(async () => {
    if (!leadInfo || messages.length === 0) return;
    setEmailSending(true);
    try {
      await sendChatTranscript({
        name: leadInfo.name,
        email: leadInfo.email,
        messages,
      });
      addMessage(emailSentMessage, "bot");
    } catch {
      addMessage(emailErrorMessage, "bot");
    } finally {
      setEmailSending(false);
    }
  }, [leadInfo, messages, addMessage]);

  const openWhatsApp = useCallback(() => {
    if (!leadInfo) return;
    const url = buildWhatsAppUrl(leadInfo, messages);
    window.open(url, "_blank", "noopener,noreferrer");
  }, [leadInfo, messages]);

  const startQuoteFlow = useCallback(() => {
    setQuoteFlow({ ...initialQuoteState, active: true });
    addMessage(quoteServicePrompt, "bot");
  }, [addMessage]);

  const selectQuoteService = useCallback(
    (serviceName) => {
      const flow = getQuoteFlow(serviceName);
      if (!flow) return;

      addMessage(serviceName, "user");

      setQuoteFlow({
        active: true,
        service: flow,
        stepIndex: 0,
        answers: {},
        result: null,
        collectingEmail: false,
        emailSent: false,
      });

      setIsTyping(true);
      setTimeout(() => {
        const firstStep = flow.steps[0];
        addMessage(`${flow.greeting}\n\n${firstStep.question}`, "bot");
        setIsTyping(false);
      }, 400);
    },
    [addMessage]
  );

  const selectQuoteOption = useCallback(
    (value) => {
      setQuoteFlow((prev) => {
        if (!prev.active || !prev.service) return prev;

        const currentStep = prev.service.steps[prev.stepIndex];
        const matchedOption = matchServiceOption(value, currentStep.options);

        if (!matchedOption) {
          setIsTyping(true);
          setTimeout(() => {
            addMessage(quoteInvalidOption, "bot");
            setIsTyping(false);
          }, 300);
          return prev;
        }

        addMessage(matchedOption.label, "user");

        const newAnswers = { ...prev.answers, [currentStep.key]: matchedOption.value };
        const nextStepIndex = prev.stepIndex + 1;

        if (nextStepIndex < prev.service.steps.length) {
          const nextStep = prev.service.steps[nextStepIndex];
          setIsTyping(true);
          setTimeout(() => {
            addMessage(nextStep.question, "bot");
            setIsTyping(false);
          }, 400 + Math.random() * 300);

          return {
            ...prev,
            answers: newAnswers,
            stepIndex: nextStepIndex,
          };
        }

        const estimate = prev.service.calculate(newAnswers);
        const summary = formatQuoteSummary(prev.service.serviceName, estimate);

        setIsTyping(true);
        setTimeout(() => {
          addMessage(summary, "bot");
          setIsTyping(false);
        }, 500 + Math.random() * 300);

        return {
          ...prev,
          answers: newAnswers,
          stepIndex: nextStepIndex,
          result: estimate,
        };
      });
    },
    [addMessage]
  );

  const sendQuoteEmailAction = useCallback(
    async (email) => {
      if (!quoteFlow.service || !quoteFlow.result) return;
      setEmailSending(true);
      try {
        await sendQuoteEmail({
          name: leadInfo?.name || "Chat Visitor",
          email,
          service: quoteFlow.service.serviceName,
          answers: quoteFlow.answers,
          estimate: quoteFlow.result,
        });
        setQuoteFlow((prev) => ({ ...prev, collectingEmail: false, emailSent: true }));
        addMessage(quoteEmailSent, "bot");
      } catch {
        addMessage(emailErrorMessage, "bot");
      } finally {
        setEmailSending(false);
      }
    },
    [quoteFlow, leadInfo, addMessage]
  );

  const openQuoteWhatsApp = useCallback(() => {
    if (!quoteFlow.service || !quoteFlow.result) return;
    const url = buildQuoteWhatsAppUrl(leadInfo, quoteFlow);
    window.open(url, "_blank", "noopener,noreferrer");
  }, [leadInfo, quoteFlow]);

  const cancelQuote = useCallback(() => {
    setQuoteFlow(initialQuoteState);
    addMessage(quoteCancelled, "bot");
  }, [addMessage]);

  const handleQuoteEmailPrompt = useCallback(() => {
    setQuoteFlow((prev) => ({ ...prev, collectingEmail: true }));
    addMessage(quoteEmailPrompt, "bot");
  }, [addMessage]);

  const handleQuoteEmailNoThanks = useCallback(() => {
    setQuoteFlow((prev) => ({ ...prev, emailSent: true }));
    addMessage(quoteEmailNoThanks, "bot");
  }, [addMessage]);

  const sendMessage = useCallback(
    (text) => {
      if (!text.trim()) return;

      addMessage(text, "user");

      if (quoteFlow.active && !quoteFlow.collectingEmail && !quoteFlow.result) {
        const normalised = text.toLowerCase().trim();
        if (normalised === "cancel" || normalised === "quit" || normalised === "exit" || normalised === "never mind" || normalised === "stop") {
          setQuoteFlow(initialQuoteState);
          setIsTyping(true);
          setTimeout(() => {
            addMessage(quoteCancelled, "bot");
            setIsTyping(false);
          }, 300);
          return;
        }

        if (!quoteFlow.service) {
          const serviceNames = getServiceNames();
          const matchedName = serviceNames.find(
            (n) => n.toLowerCase() === normalised || n.toLowerCase().includes(normalised)
          );
          if (matchedName) {
            selectQuoteService(matchedName);
          } else {
            setIsTyping(true);
            setTimeout(() => {
              addMessage(
                `I didn't recognise that service. Please choose from the options above:\n\n${serviceNames.map((n) => `• ${n}`).join("\n")}`,
                "bot"
              );
              setIsTyping(false);
            }, 300);
          }
          return;
        }

        if (quoteFlow.service && !quoteFlow.result) {
          selectQuoteOption(text);
          return;
        }
      }

      if (quoteFlow.collectingEmail) {
        const emailRegex = /[\w.+-]+@[\w-]+\.[\w.]+/;
        const match = text.match(emailRegex);
        if (match) {
          sendQuoteEmailAction(match[0]);
        } else {
          setIsTyping(true);
          setTimeout(() => {
            addMessage("Please enter a valid email address (e.g. your@email.com).", "bot");
            setIsTyping(false);
          }, 300);
        }
        return;
      }

      if (isCollectingLead) {
        const parsed = parseLeadInfo(text);
        if (parsed) {
          setLeadInfo(parsed);
          setIsCollectingLead(false);
          setIsTyping(true);
          setTimeout(() => {
            addMessage(leadConfirm(parsed.name), "bot");
            setIsTyping(false);
          }, 400);
        } else {
          setIsTyping(true);
          setTimeout(() => {
            addMessage(invalidLeadInfo, "bot");
            setIsTyping(false);
          }, 400);
        }
        return;
      }

      const inputTokens = tokenize(text);

      if (matchQuoteTrigger(inputTokens) && !quoteFlow.active) {
        startQuoteFlow();
        return;
      }

      if (matchHumanHandoff(inputTokens) && !leadInfo) {
        setIsCollectingLead(true);
        setIsTyping(true);
        setTimeout(() => {
          addMessage(leadPrompt, "bot");
          setIsTyping(false);
        }, 400);
        return;
      }

      setIsTyping(true);
      const delay = 400 + Math.random() * 400;
      setTimeout(() => {
        const response = generateResponse(text);
        addMessage(response, "bot");
        setIsTyping(false);
      }, delay);
    },
    [
      addMessage,
      isCollectingLead,
      leadInfo,
      quoteFlow,
      selectQuoteService,
      selectQuoteOption,
      sendQuoteEmailAction,
      startQuoteFlow,
    ]
  );

  return {
    messages,
    isOpen,
    isTyping,
    leadInfo,
    isCollectingLead,
    emailSending,
    quoteFlow,
    toggleChat,
    closeChat,
    sendMessage,
    clearChat,
    sendEmailTranscript,
    openWhatsApp,
    selectQuoteService,
    selectQuoteOption,
    cancelQuote,
    handleQuoteEmailPrompt,
    handleQuoteEmailNoThanks,
    openQuoteWhatsApp,
  };
}
